import { catchError, from, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from '../../general/exceptions-service';
import { HttpClientService } from '../../general/http-client-service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InventoryAgeBucketDTO, InventoryDeviceCountByModelResultDTO, InventoryDeviceCountByStatusDTO, InventoryDeviceDetailDTO, InventoryLinkStatusDTO, InventoryRegistrationHistoryDTO, InventoryWarrantyBucketDTO } from 'src/app/core/models/system/inventory/inventory.dto';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

const ENC_COMPANY_ID_REUSE_MS=60000;

@Injectable({ providedIn: 'root' })

export class InventoryService {

  private encCompanyIdCache: { companyId: number; expires: number; value$: Observable<string> } | undefined;

  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  private buildPath(path: string, companyIdEncSS: string, extraParams: string): string {
    try {
      return environment.eSimApi
             + eSimApiPaths.Inventory
             + '/'
             + path
             + '?companyIdEncSS=' + encodeURIComponent(companyIdEncSS)
             + extraParams;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  private encryptCompanyId(companyId: number): Observable<string> {
    try {
      const cached=this.encCompanyIdCache;

      if (cached && cached.companyId===companyId && Date.now()<cached.expires)
        return cached.value$;

      const value$=this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
                   .pipe(
                     map((encResult: StringResultDTO) => encResult?.result ?? ''),
                     catchError((error) => {
                       if (this.encCompanyIdCache?.value$===value$)
                         this.encCompanyIdCache=undefined;

                       return throwError(() => error);
                     }),
                     shareReplay({ bufferSize: 1, refCount: false })
                   );

      this.encCompanyIdCache={ companyId: companyId, expires: Date.now() + ENC_COMPANY_ID_REUSE_MS, value$: value$ };

      return value$;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  private getWithCompany<T>(methodName : string,
                            companyId  : number,
                            path       : string,
                            extraParams: string): Observable<T> {
    try {
      return this.encryptCompanyId(companyId)
             .pipe(
               switchMap((companyIdEncSS: string) => this.httpClientService.get<T>(this.buildPath(path, companyIdEncSS, extraParams))),
               catchError((error) => {
                 return throwError(() => new Error('InventoryService.' + methodName + '() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  private async parseBlobError(error: HttpErrorResponse): Promise<string> {
    try {
      const contentType=(error.headers?.get('content-type') ?? '').toLowerCase();

      if (contentType.startsWith('application/json') && error.error instanceof Blob) {
        const text   =await error.error.text();
        const parsed =<{ errorMessage?: string; error?: string; message?: string }>JSON.parse(text);
        const message=parsed?.errorMessage ?? parsed?.message ?? parsed?.error;

        if (message)
          return 'InventoryService.downloadExport() - ' + message;
      }

      return 'InventoryService.downloadExport() - The export could not be generated (HTTP ' + error.status + ').';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'InventoryService.downloadExport() - The export could not be generated.';
    }
  }

  private statusParam(status: string | undefined): string {
    try {
      return status ? '&status=' + encodeURIComponent(status) : '';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  private triggerDownload(response: HttpResponse<Blob>, format: string): void {
    try {
      const body=response.body;

      if (!body)
        throw new Error('InventoryService.triggerDownload() - The response contained no export data.');

      const disposition=response.headers?.get('content-disposition') ?? '';
      const matched    =/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const fileName   =matched?.[1] ?? ('SureSIM_Inventory_' + new Date().toISOString().slice(0, 10) + '.' + format);
      const objectUrl  =URL.createObjectURL(body);
      const anchor     =document.createElement('a');

      anchor.href    =objectUrl;
      anchor.download=fileName;
      anchor.click();

      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public buildDevicesServerSidePath(companyId: number, status: string | undefined): Observable<string> {
    try {
      return this.encryptCompanyId(companyId)
             .pipe(
               map((companyIdEncSS: string) => this.buildPath(eSimApiPaths.InventoryGetDevicesServerSide, companyIdEncSS, this.statusParam(status)))
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public downloadExport(companyId: number, format: string, status: string | undefined): Observable<HttpResponse<Blob>> {
    try {
      return this.encryptCompanyId(companyId)
             .pipe(
               switchMap((companyIdEncSS: string) => {
                 return this.httpClientService.getBlob(this.buildPath(eSimApiPaths.InventoryGetDeviceExport,
                                                                      companyIdEncSS,
                                                                      '&format=' + encodeURIComponent(format) + this.statusParam(status)))
                        .pipe(
                          catchError((error: HttpErrorResponse) => {
                            return from(this.parseBlobError(error))
                                   .pipe(switchMap(message => throwError(() => message)));
                          })
                        );
               }),
               switchMap((response: HttpResponse<Blob>) => {
                 this.triggerDownload(response, format);

                 return of(response);
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getDeviceByAssetId(companyId: number, assetId: string): Observable<InventoryDeviceDetailDTO> {
    try {
      return this.getWithCompany<InventoryDeviceDetailDTO>('getDeviceByAssetId', companyId, eSimApiPaths.InventoryGetDeviceByAssetId,
                                                           '&assetId=' + encodeURIComponent(assetId));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getDeviceCountByAge(companyId: number): Observable<InventoryAgeBucketDTO[]> {
    try {
      return this.getWithCompany<InventoryAgeBucketDTO[]>('getDeviceCountByAge', companyId, eSimApiPaths.InventoryGetDeviceCountByAge, '');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getDeviceCountByModel(companyId: number, limit: number): Observable<InventoryDeviceCountByModelResultDTO> {
    try {
      return this.getWithCompany<InventoryDeviceCountByModelResultDTO>('getDeviceCountByModel', companyId, eSimApiPaths.InventoryGetDeviceCountByModel,
                                                           '&limit=' + limit);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getDeviceCountByStatus(companyId: number): Observable<InventoryDeviceCountByStatusDTO> {
    try {
      return this.getWithCompany<InventoryDeviceCountByStatusDTO>('getDeviceCountByStatus', companyId, eSimApiPaths.InventoryGetDeviceCountByStatus, '');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getDeviceCountByWarrantyExpiry(companyId: number): Observable<InventoryWarrantyBucketDTO[]> {
    try {
      return this.getWithCompany<InventoryWarrantyBucketDTO[]>('getDeviceCountByWarrantyExpiry', companyId, eSimApiPaths.InventoryGetDeviceCountByWarrantyExpiry, '');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getDeviceRegistrationHistory(companyId: number, groupBy: string, from: string): Observable<InventoryRegistrationHistoryDTO[]> {
    try {
      return this.getWithCompany<InventoryRegistrationHistoryDTO[]>('getDeviceRegistrationHistory', companyId, eSimApiPaths.InventoryGetDeviceRegistrationHistory,
                                                                    '&groupBy=' + encodeURIComponent(groupBy) + '&from=' + encodeURIComponent(from));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getLinkStatus(companyId: number): Observable<InventoryLinkStatusDTO> {
    try {
      return this.getWithCompany<InventoryLinkStatusDTO>('getLinkStatus', companyId, eSimApiPaths.InventoryGetLinkStatus, '');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }
}
